/**
 * insightPhrase — compose the punter-facing sentence for one "jewel".
 *
 * Insights engine Phase 5. The engine ships each finding as language-neutral
 * `parts` (subject, value, baseline, field path, mode, …) plus an English
 * `rationale` fallback. This module turns `parts` into a localised sentence
 * using whole-sentence i18n templates (one per scoring mode) so translators
 * work on complete sentences, never glued fragments.
 *
 * Language model (English is the baseline; da/sv are translations):
 *   - The SENTENCE template comes from `insights.phrase.*` per locale.
 *   - The STAT NOUN ("possession", "corners") comes from `insights.stat.<key>`
 *     per locale, keyed off the trailing segment of the Sportradar field path.
 *     If a locale hasn't translated a stat key, we fall back to the operator's
 *     catalog label (Danish today), then to a humanised key. We NEVER drop a
 *     Danish noun into an English sentence silently — the dictionary is the
 *     authoritative source and the catalog label is only a last resort.
 *
 * Pure + deterministic. No React, no network. Unit-tested in
 * insightPhrase.test.ts.
 */

import type { MatchInsight, MatchInsightParts } from '../../api/createEngineClient.js';

/** Minimal shape of i18next's `t` we depend on. */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Format a number: ints bare, else ≤2 dp trimmed, with the locale's decimal
 *  separator (comma for sv/da, dot for en). */
function formatNum(n: number, locale = 'en'): string {
  if (!Number.isFinite(n)) return String(n);
  const s = Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
  return locale.slice(0, 2).toLowerCase() === 'en' ? s : s.replace('.', ',');
}

/** Percent for fraction-valued (streak) findings. */
function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

/** English fallback nouns for the tier-scoped baseline templates (0163).
 *  The real per-locale sentences live in cms.ui_strings
 *  (`insights.phrase.rateTier.*` / `insights.phrase.streakTier.*`). */
const TIER_NOUN_EN: Record<'top' | 'middle' | 'bottom', string> = {
  top: 'top-third',
  middle: 'mid-table',
  bottom: 'bottom-third',
};

/**
 * Locale-aware ordinal. English gets 1st/2nd/3rd/…; da/sv use the "1."
 * convention. Anything else falls back to the bare number.
 */
function ordinal(n: number, locale: string): string {
  const lang = locale.slice(0, 2).toLowerCase();
  // English needs the st/nd/rd/th suffix. da/sv (and anything else) take
  // the raw number — their rank templates carry the positional wording
  // ("nr. 15", "15 av 16"), so an appended "." would read as "nr. 15.".
  if (lang === 'en') {
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  }
  return String(n);
}

/** Trailing stat key from a Sportradar field path, e.g.
 *  `competitor.statistics.ball_possession` → `ball_possession`. */
export function statKeyFromPath(fieldPath: string): string {
  const last = fieldPath.split('.').pop() ?? fieldPath;
  return last.replace(/\[\]/g, '').trim();
}

/** Humanise a raw key as the absolute last resort: `corner_kicks` → `corner kicks`. */
function humanise(key: string): string {
  return key.replace(/[_.]+/g, ' ').trim();
}

/**
 * Resolve the localised stat noun for a field. Order:
 *   1. `insights.stat.<key>` in the active locale (authoritative),
 *   2. operator catalog label (`parts.fieldLabel`, Danish today),
 *   3. humanised key.
 */
export function resolveStatLabel(parts: MatchInsightParts, t: TranslateFn): string {
  const key = statKeyFromPath(parts.fieldPath);
  const dictKey = `insights.stat.${key}`;
  const fromDict = t(dictKey, { defaultValue: '' });
  // i18next ignores an empty-string defaultValue and returns the KEY on a
  // miss — so guard against the raw key leaking to the punter ("insights.stat.
  // goals_conceded_second_half"). Only accept a real translation.
  if (fromDict && fromDict.trim() !== '' && fromDict !== dictKey) return fromDict;
  if (parts.fieldLabel && parts.fieldLabel.trim() !== '') return parts.fieldLabel;
  return humanise(key);
}

/**
 * Compose the localised jewel sentence. Falls back to the engine's English
 * `rationale` when structured parts are absent (legacy plans) or when the
 * value isn't numeric.
 */
export function buildInsightPhrase(
  insight: MatchInsight,
  t: TranslateFn,
  locale: string,
): string {
  const parts = insight.parts;
  if (!parts || typeof parts.value !== 'number') {
    return insight.rationale;
  }

  // Streak/recency gems ("N of their last M") — baseline-free count facts.
  if (parts.eventKey && typeof parts.count === 'number' && typeof parts.ofN === 'number') {
    const ek = parts.eventKey;
    if (ek === 'h2h_won') {
      return t('insights.phrase.h2hWon', {
        defaultValue: '{{subject}} have won {{count}} of the last {{ofN}} meetings',
        subject: parts.subject, count: parts.count, ofN: parts.ofN,
      });
    }
    const labelKey = ek.replace(/^h2h_/, '');
    const event = t(`insights.event.${labelKey}`, { defaultValue: labelKey });
    if (ek.startsWith('h2h_')) {
      return t('insights.phrase.h2hCount', {
        defaultValue: '{{event}} in {{count}} of the last {{ofN}} meetings',
        event, count: parts.count, ofN: parts.ofN,
      });
    }
    return t('insights.phrase.countOfN', {
      defaultValue: '{{subject}}: {{event}} in {{count}} of their last {{ofN}}',
      subject: parts.subject, event, count: parts.count, ofN: parts.ofN,
    });
  }

  const field = resolveStatLabel(parts, t);
  const subject = parts.subject;

  switch (parts.kind) {
    case 'rank-position': {
      if (parts.rank == null || parts.rankOf == null) break;
      return t('insights.phrase.rank', {
        defaultValue: '{{subject}} are {{rank}} of {{rankOf}} in the league',
        subject,
        rank: ordinal(parts.rank, locale),
        rankOf: parts.rankOf,
      });
    }
    case 'differential': {
      if (parts.opponent == null) break;
      return t('insights.phrase.differential', {
        defaultValue: "{{subject}} {{value}} {{field}} vs the opponent's {{opponent}}",
        subject,
        value: formatNum(parts.value, locale),
        opponent: formatNum(parts.opponent, locale),
        field,
      });
    }
    case 'streak': {
      if (parts.baseline != null) {
        // Tier-scoped baseline (0163) — whole-sentence template per third
        // so translators never glue fragments.
        if (parts.baselineTier) {
          return t(`insights.phrase.streakTier.${parts.baselineTier}`, {
            defaultValue: `{{subject}} {{value}} on {{field}} (${TIER_NOUN_EN[parts.baselineTier]} avg {{baseline}})`,
            subject,
            value: pct(parts.value),
            baseline: pct(parts.baseline),
            field,
          });
        }
        return t('insights.phrase.streak', {
          defaultValue: '{{subject}} {{value}} on {{field}} (league avg {{baseline}})',
          subject,
          value: pct(parts.value),
          baseline: pct(parts.baseline),
          field,
        });
      }
      return t('insights.phrase.streakNoBaseline', {
        defaultValue: '{{subject}} {{value}} on {{field}}',
        subject,
        value: pct(parts.value),
        field,
      });
    }
    case 'rate-vs-mean':
    default: {
      // Neutral phrasing — NO "average" verb. The same mode covers both
      // cumulative totals and per-match rates, so asserting "average" was
      // wrong for totals (e.g. season points). State value + field; the
      // league-avg comparison stays. (Per-source units like "per game"
      // arrive in the deep-optimization phase.)
      if (parts.baseline != null) {
        if (parts.baselineTier) {
          return t(`insights.phrase.rateTier.${parts.baselineTier}`, {
            defaultValue: `{{subject}} {{value}} {{field}} (${TIER_NOUN_EN[parts.baselineTier]} avg {{baseline}})`,
            subject,
            value: formatNum(parts.value, locale),
            baseline: formatNum(parts.baseline, locale),
            field,
          });
        }
        return t('insights.phrase.rate', {
          defaultValue: '{{subject}} {{value}} {{field}} (league avg {{baseline}})',
          subject,
          value: formatNum(parts.value, locale),
          baseline: formatNum(parts.baseline, locale),
          field,
        });
      }
      return t('insights.phrase.rateNoBaseline', {
        defaultValue: '{{subject}} {{value}} {{field}}',
        subject,
        value: formatNum(parts.value, locale),
        field,
      });
    }
  }

  // Any mode that hit a `break` (missing required part) degrades to the
  // engine's English rationale rather than emit a half-built sentence.
  return insight.rationale;
}

/**
 * Optional "lower is better" hint for the UI — true when the field is one
 * where a low number is the good outcome (goals conceded, xGA). Lets the
 * widget colour the jewel correctly without re-deriving direction.
 */
export function isLowerBetter(insight: MatchInsight): boolean {
  return insight.parts?.higherIsBetter === false;
}
